import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { PRICE_INPUT_WIDTH } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-constants';
import { Tooltip } from '@usepepper/sprout-kit-ui';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const INDICATOR_SIZE = 8;
const INDICATOR_HOLLOW_BORDER_WIDTH = 1.5;
const TOOLTIP_BORDER_WIDTH = 1;
const TOOLTIP_CONTENT_GAP = 2;

interface PriceOverrideInfoProps {
  defaultSecondaryValue?: string;
  defaultValue: string;
  editedByName: string | null;
  expiryDateDisplay?: string;
  isInline?: boolean;
  lastValueLabelKey?: string;
  showOverrideIndicator?: boolean;
  showTemporaryIndicator?: boolean;
}

export const PriceOverrideInfoDisplay: React.FC<PriceOverrideInfoProps> = ({
  defaultSecondaryValue,
  defaultValue,
  editedByName,
  expiryDateDisplay,
  lastValueLabelKey,
  showTemporaryIndicator = false,
  isInline = false,
  showOverrideIndicator = true,
}) => {
  const [styles, theme] = useStyles();

  const showTemporary = showTemporaryIndicator && showOverrideIndicator;

  // Temporary edits show the expiry date in place of the default value inline
  // and reveal the regular value on hover. Other overrides keep the default
  // value inline with its secondary value trailing. For example, a price edit:
  //    Permanent: ● $50.00/bag   $250.00
  //    Temporary: ○ Jun 15
  const primaryValue = showTemporary ? expiryDateDisplay : defaultValue;
  const secondaryValue = showTemporary ? undefined : defaultSecondaryValue;

  if (!primaryValue && !showOverrideIndicator) {
    return null;
  }

  // Temporary edits instead display the regular value on hover
  const lastValueHoverText =
    showTemporary && defaultValue && lastValueLabelKey
      ? i18n.t(lastValueLabelKey, { value: defaultValue })
      : null;

  const defaultValueDisplay = (
    <View
      style={[
        styles.indicatorContainer,
        isInline && styles.indicatorContainerInline,
      ]}
    >
      {showOverrideIndicator ? (
        <View
          accessibilityLabel={
            showTemporary
              ? i18n.t(
                  'sales_portal.order_entry.table.temporary_adjustment.indicator_a11y_label',
                )
              : i18n.t('sales_portal.order_entry.table.price_override_active')
          }
          accessibilityRole="image"
          style={showTemporary ? styles.indicatorHollow : styles.indicator}
        />
      ) : null}
      <Typography
        color={theme.sk.color.text.tertiary}
        numberOfLines={1}
        variant="paragraph-sm"
      >
        {primaryValue}
      </Typography>
    </View>
  );

  return (
    <View
      accessibilityLabel={showOverrideIndicator ? primaryValue : undefined}
      style={styles.row}
    >
      {showOverrideIndicator ? (
        <Tooltip placement="top">
          <Tooltip.Trigger>{defaultValueDisplay}</Tooltip.Trigger>
          <Tooltip.Content
            alignItems="flex-start"
            animation={null}
            backgroundColor="$bgPrimaryDefault"
            borderColor="$borderPrimary"
            borderRadius="$8"
            borderWidth={TOOLTIP_BORDER_WIDTH}
            gap={TOOLTIP_CONTENT_GAP}
            paddingHorizontal="$8"
            paddingVertical="$4"
          >
            {lastValueHoverText ? (
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-sm"
              >
                {lastValueHoverText}
              </Typography>
            ) : null}
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm"
            >
              {i18n.t('sales_portal.order_entry.table.updated_by', {
                name:
                  editedByName ??
                  i18n.t('sales_portal.order_entry.table.updated_by.system'),
              })}
            </Typography>
          </Tooltip.Content>
        </Tooltip>
      ) : (
        defaultValueDisplay
      )}
      {secondaryValue ? (
        <Typography
          color={theme.sk.color.text.tertiary}
          numberOfLines={1}
          variant="paragraph-sm"
        >
          {secondaryValue}
        </Typography>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  indicator: {
    backgroundColor: theme.sk.color.icon.warning.secondary,
    borderRadius: INDICATOR_SIZE / 2,
    flexShrink: 0,
    height: INDICATOR_SIZE,
    width: INDICATOR_SIZE,
  },
  indicatorContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'flex-end',
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    width: PRICE_INPUT_WIDTH,
  },
  indicatorContainerInline: {
    justifyContent: 'flex-start',
    paddingHorizontal: 0,
    width: 'auto',
  },
  indicatorHollow: {
    backgroundColor: 'transparent',
    borderColor: theme.sk.color.icon.warning.secondary,
    borderRadius: INDICATOR_SIZE / 2,
    borderWidth: INDICATOR_HOLLOW_BORDER_WIDTH,
    flexShrink: 0,
    height: INDICATOR_SIZE,
    width: INDICATOR_SIZE,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
